/**
 * Suivi individuel des référents (§8.2) : où en est chacun, et à quelle distance
 * du groupe il se situe.
 *
 * L'écart-type porté par le référentiel dit qu'il y a désaccord ; il ne dit pas
 * QUI est atypique. Sans cette liste, la seule action possible face à un
 * désaccord est de tout rouvrir. Avec elle, on sait quelle contribution reprendre
 * — d'où le tri par proximité croissante et le bouton de réouverture par ligne.
 */
import { Fragment, useState } from "react";
import { useAction, useMutation } from "convex/react";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  Mail,
  RotateCcw,
  Trash2,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import {
  ReferentContribution,
  type Consensus,
  type EchelleReferentiel,
} from "./ReferentContribution";
import { dateCourte } from "@repo/utils";

type Referent = {
  parcoursEvaluateurId: Id<"parcoursEvaluateur">;
  notationId: Id<"notation">;
  nom?: string;
  prenom?: string;
  email?: string;
  anonyme?: boolean;
  lienEnvoye?: boolean;
  /** Ce référent est l'utilisateur courant : son lien lui est rendu en clair. */
  estMoi: boolean;
  lienAcces: string | null;
  statut: "non_commence" | "en_cours" | "termine";
  inviteLe: number;
  derniereActiviteLe: number;
  soumisLe: number | null;
  avancement: { renseignees: number; total: number };
  contribution: Record<string, unknown> | null;
  estBrouillon: boolean;
  proximite: number | null;
  ecarts: { code: string; ecart: number }[];
};

type Props = {
  referents: Referent[];
  dimensions: { code: string; label: string }[];
  consensus: Consensus;
  echelle: EchelleReferentiel;
  /** Pilote le vocabulaire : « remplacer » quand le référent est unique (PAMI),
   *  « retirer » quand il n'est qu'un contributeur parmi d'autres. */
  cible: "pami" | "pc-fit";
  /** Proximité attendue au hasard (Q-sort) : repère sans lequel un 55 % se lit
   *  « moyen » alors qu'il est excellent sur une échelle ipsative. */
  repereHasard: number | null;
  /** Portail admin : consultation seule, aucune relance ni réouverture. */
  lectureSeule?: boolean;
};

const BADGE: Record<
  Referent["statut"],
  { texte: string; variant: "success" | "warning" | "purple" }
> = {
  termine: { texte: "A répondu", variant: "success" },
  en_cours: { texte: "En cours", variant: "warning" },
  non_commence: { texte: "En attente", variant: "purple" },
};

const nomAffiche = (r: Referent) =>
  r.anonyme
    ? "Référent anonyme"
    : [r.prenom, r.nom].filter(Boolean).join(" ") || r.email || "Référent";

export function ReferentsSuiviCard({
  referents,
  dimensions,
  consensus,
  echelle,
  cible,
  repereHasard,
  lectureSeule = false,
}: Props) {
  const envoyerLien = useAction(api.email.envoyerLienEvaluateur);
  const rouvrir = useMutation(api.referentiel.campagne.rouvrirNotationReferent);
  const retirer = useMutation(api.referentiel.campagne.retirerReferent);

  const [ouvert, setOuvert] = useState<Id<"notation"> | null>(null);
  const [enCoursId, setEnCoursId] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const nbRepondants = referents.filter((r) => r.statut === "termine").length;
  // Référent unique : la « proximité au consensus » compare la contribution à
  // elle-même. Toujours 100 %, donc trompeuse — la colonne saute.
  const referentUnique = cible === "pami";

  /** Une seule action en vol à la fois : `enCoursId` sert aussi de verrou. */
  const executer = async (cle: string, action: () => Promise<unknown>) => {
    setErreur(null);
    setEnCoursId(cle);
    try {
      await action();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setEnCoursId(null);
    }
  };

  const relancer = (r: Referent) =>
    executer(r.parcoursEvaluateurId, () =>
      envoyerLien({ parcoursEvaluateurId: r.parcoursEvaluateurId }),
    );

  const rouvrirContribution = (r: Referent) =>
    executer(r.notationId, () => rouvrir({ notationId: r.notationId }));

  const retirerReferent = (r: Referent) =>
    executer(r.parcoursEvaluateurId, () =>
      retirer({ parcoursEvaluateurId: r.parcoursEvaluateurId }),
    );

  if (referents.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {referentUnique ? "Référent de la campagne" : "Suivi des référents"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {referentUnique
              ? "Aucun référent désigné : le référentiel ne peut pas être rempli, et aucun score de matching n'est calculable."
              : "Aucun référent invité pour l'instant."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {referentUnique
            ? "Référent de la campagne"
            : `Suivi des référents (${nbRepondants}/${referents.length} ont répondu)`}
        </CardTitle>
        <CardDescription>
          {referentUnique ? (
            <>
              Une seule personne remplit ce référentiel. Tant que sa
              contribution n'est pas validée, vous pouvez la confier à quelqu'un
              d'autre.
            </>
          ) : (
            <>
              Trié du plus atypique au plus proche du groupe. La proximité se lit
              comme un score de matching candidat — même distance, même échelle
              tassée
              {repereHasard !== null && (
                <>
                  {" "}
                  : deux classements indépendants valent déjà{" "}
                  {Math.round(repereHasard)} %
                </>
              )}
              .
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {erreur && <p className="text-sm text-destructive">{erreur}</p>}

        {/* `-mx-1 px-1` : place pour le contour de focus des boutons de la
            première colonne, que le défilement horizontal recadrait. */}
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Référent</th>
                <th className="py-2 pr-3 font-medium">Statut</th>
                <th className="py-2 pr-3 font-medium">Avancement</th>
                {!referentUnique && (
                  <th className="py-2 pr-3 font-medium">Proximité</th>
                )}
                <th className="py-2 pr-3 font-medium">Invité</th>
                <th className="py-2 pr-3 font-medium">Dernière activité</th>
                <th className="py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {referents.map((r) => {
                const estOuvert = ouvert === r.notationId;
                return (
                  <Fragment key={r.notationId}>
                    <tr className="border-b last:border-0">
                      <td className="py-2 pr-3">
                        <button
                          className="flex items-center gap-1 text-left font-medium hover:text-primary"
                          onClick={() =>
                            setOuvert(estOuvert ? null : r.notationId)
                          }
                        >
                          {estOuvert ? (
                            <ChevronDown className="h-4 w-4 shrink-0" />
                          ) : (
                            <ChevronRight className="h-4 w-4 shrink-0" />
                          )}
                          {nomAffiche(r)}
                        </button>
                        {r.email && !r.anonyme && (
                          <p className="pl-5 text-xs text-muted-foreground">
                            {r.email}
                          </p>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant={BADGE[r.statut].variant}>
                          {BADGE[r.statut].texte}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 tabular-nums">
                        {r.avancement.renseignees}/{r.avancement.total}
                      </td>
                      {!referentUnique && (
                        <td className="py-2 pr-3 tabular-nums">
                          {r.proximite !== null ? (
                            `${Math.round(r.proximite)} %`
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      )}
                      <td className="py-2 pr-3 tabular-nums text-muted-foreground">
                        {dateCourte(r.inviteLe)}
                      </td>
                      <td className="py-2 pr-3 tabular-nums text-muted-foreground">
                        {r.soumisLe
                          ? `Validé le ${dateCourte(r.soumisLe)}`
                          : dateCourte(r.derniereActiviteLe)}
                      </td>
                      <td className="py-2">
                        <div className="flex flex-wrap gap-2">
                          {lectureSeule && (
                            <span className="text-xs text-muted-foreground">
                              —
                            </span>
                          )}
                          {/* Son propre lien : pas d'aller-retour par email
                              pour se donner accès à soi-même. */}
                          {!lectureSeule &&
                            r.lienAcces &&
                            r.statut !== "termine" && (
                              <Button variant="outline" size="sm" asChild>
                                <a
                                  href={r.lienAcces}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <ExternalLink className="mr-2 h-4 w-4" />
                                  Remplir
                                </a>
                              </Button>
                            )}
                          {!lectureSeule && r.email && !r.estMoi && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={enCoursId === r.parcoursEvaluateurId}
                              onClick={() => void relancer(r)}
                            >
                              {enCoursId === r.parcoursEvaluateurId ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Mail className="mr-2 h-4 w-4" />
                              )}
                              {r.lienEnvoye ? "Relancer" : "Envoyer le lien"}
                            </Button>
                          )}
                          {/* Retrait possible TANT QUE rien n'est validé : une
                              contribution validée alimente la projection, la
                              supprimer ferait bouger les scores sans trace. */}
                          {!lectureSeule && r.statut !== "termine" && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={enCoursId === r.parcoursEvaluateurId}
                              onClick={() => void retirerReferent(r)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {cible === "pami" ? "Remplacer" : "Retirer"}
                            </Button>
                          )}
                          {!lectureSeule && r.statut === "termine" && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={enCoursId === r.notationId}
                              onClick={() => void rouvrirContribution(r)}
                            >
                              {enCoursId === r.notationId ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <RotateCcw className="mr-2 h-4 w-4" />
                              )}
                              Rouvrir
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {estOuvert && (
                      <tr className="border-b">
                        <td colSpan={referentUnique ? 6 : 7} className="bg-muted/30 p-4">
                          <ReferentContribution
                            dimensions={dimensions}
                            contribution={r.contribution}
                            consensus={consensus}
                            echelle={echelle}
                            ecarts={r.ecarts}
                            estBrouillon={r.estBrouillon}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
